import { TicketTypeSelectItem } from "@/components/tickets";
import type { ExistingFileController } from "@/hooks/use-existing-file-controller";
import { useMentionOptions } from "@/hooks/use-mention-options";
import type { ResourceLinkController } from "@/hooks/use-resource-link-controller";
import { useProject } from "@/hooks/use-project";
import { useTicketTypes } from "@/hooks/use-ticket-types";
import { DateInput } from "@msuite/ui/date-input";
import { Divider } from "@msuite/ui/divider";
import type { FileUploadController } from "@msuite/ui/file-upload";
import { NumberInput, Select, Textarea, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { countWorkingDays } from "@msuite/utils/date";
import type { TicketFormSchema } from "@msuite/utils/schemas";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { TicketFormAdvanced } from "./ticket-form-advanced";
import { TicketFormAssignee } from "./ticket-form-assignee";

/** Props Interface */
interface TicketFormProps {
	controller: FileUploadController;
	ticketId?: string | null;
	resourceLinkController?: ResourceLinkController;
	existingFileController?: ExistingFileController;
}

export function TicketForm({
	controller,
	ticketId,
	resourceLinkController,
	existingFileController,
}: TicketFormProps) {
	/** Context */
	const methods = useFormContext<TicketFormSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const { data } = useTicketTypes();
	const mentionsOptions = useMentionOptions();

	/** Computed */
	const watchedMode = (methods.watch("dateMode") ?? "date") as "date" | "week";
	const watchedProjectId = methods.watch("projectId");

	/** Query */
	// The site's own week, which is what an estimate is counted in. The form is opened on a project
	// and the picker can move it to another, so this follows the field rather than a prop.
	const { project } = useProject({
		projectId: watchedProjectId === "NULL" ? null : watchedProjectId,
	});

	/** Effects */
	// Carried into the form values so the resolver measures the ceiling in the same week the input
	// below shows. Nothing writes the field — the handler clamps against the stored project — but
	// the two disagreeing would refuse an estimate the site can plainly hold.
	useEffect(() => {
		if (!project?.weekdays) return;
		methods.setValue("projectWeekdays", project.weekdays);
	}, [project?.weekdays, methods]);

	// The ceiling the estimate is validated against, shown on the input as well so the limit is
	// visible while typing rather than only on submit. It follows the two date fields, because
	// narrowing the window is the usual way an estimate becomes too large.
	const watchedStartDate = methods.watch("startDate");
	const watchedEndDate = methods.watch("endDate");
	// Floored at one for the same reason the schema floors it: a weekend-only window holds no
	// working day, and the field still has to be answerable.
	const availableWorkingDays =
		watchedStartDate && watchedEndDate
			? Math.max(1, countWorkingDays(watchedStartDate, watchedEndDate, project?.weekdays))
			: null;

	if (!data) return null;

	/** Render */
	return (
		<div className="form-container">
			<Select
				isRequired
				isNullable={false}
				label="Typ"
				defaultValue={data.tableTypes[0]?.id}
				options={
					data?.tableTypes?.map((ticketType) => ({
						label: ticketType.name,
						value: ticketType.id,
						item: ticketType,
					})) ?? []
				}
				control={methods.control}
				name="type"
				renderOption={(item) => <TicketTypeSelectItem ticketType={item} />}
				autoFocus
				itemNotFoundLabel={t("projects.ticket_type_not_found")}
				data-cy="ticket-type-select"
			/>
			<TextInput
				isRequired
				label={t("projects.ticket")}
				placeholder={t("placeholders.ticket_name")}
				control={methods.control}
				name="description"
				data-cy="ticket-description-input"
			/>
			<TicketFormAssignee />
			<Divider variant="modal" />
			<SegmentedControl
				value={watchedMode}
				onChange={(v) => methods.setValue("dateMode", v)}
				items={[
					{ value: "date", label: t("dates.date"), cyId: "date-mode-date" },
					{ value: "week", label: t("dates.week"), cyId: "date-mode-week" },
				]}
			/>
			{/* Both ends are mandatory — the columns are NOT NULL — so a one-day ticket carries the
			    same date twice rather than one date and a gap. */}
			<div className="grid grid-cols-2 gap-4">
				<DateInput
					label={t("dates.start_date")}
					control={methods.control}
					name="startDate"
					isRequired
				/>
				<DateInput
					label={t("dates.end_date")}
					control={methods.control}
					name="endDate"
					isRequired
				/>
			</div>
			<NumberInput
				label={t("projects.estimated_days")}
				helperText={t("projects.estimated_days_helper_text")}
				control={methods.control}
				name="estimatedDays"
				isRequired
				placeholder={t("placeholders.estimated_days")}
				step={1}
				min={1}
				max={availableWorkingDays ?? undefined}
			/>
			<Divider variant="modal" />
			<Textarea
				label={t("hints.hint")}
				placeholder={t("placeholders.add_note")}
				control={methods.control}
				name="note"
				isRequired={false}
				disableFormatting
				data-cy="ticket-note-input"
				{...mentionsOptions}
			/>
			<TicketFormAdvanced
				controller={controller}
				ticketId={ticketId}
				resourceLinkController={resourceLinkController}
				existingFileController={existingFileController}
			/>
		</div>
	);
}
